import {
  Button,
  Header,
  Loader,
  MatchUp,
  Paper,
  ScrollContainer,
  StickyFooterButtons,
  Typography,
  useLoading,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  addEventBlackout,
  cancelDayOfGame,
  deleteEventBlackout,
  getDepartmentsByEvent,
  getEvent,
  removeDayOfCancellation,
} from "../../../api/RequesTixApi";
import EventAccess from "../../../components/events/EventAccess";
import RouteConstants from "../../../constants/RouteConstants";
import { DepartmentByGameDTO } from "../../../types/Department";
import { GameDTO, GameTime } from "../../../types/Game";
import { RequestixLocationState } from "../../../types/Routing";
import { fetchAvailableDigitalTicketCount } from "../../../util/DigitalTicketUtil";
import { useTimezone as getTimezone } from "../../../util/TimeUtil";

type AdminEditEventUrlProps = {
  eventId: string;
};

type EventActionsProps = {
  event: GameDTO;
  close: () => void;
  blackoutGame: () => Promise<void>;
  removeBlackout: () => Promise<void>;
  cancelDayOf: () => Promise<void>;
  removeDayOfCancel: () => Promise<void>;
};

const EventActions = ({
  event,
  close,
  blackoutGame,
  removeBlackout,
  cancelDayOf,
  removeDayOfCancel,
}: EventActionsProps) => (
  <div className="d-flex align-items-center justify-content-end">
    <StickyFooterButtons startsOpen={true}>
      {!event.blackedOut ? (
        <Button variant="default" onClick={blackoutGame}>
          Blackout Game
        </Button>
      ) : (
        <Button variant="primary" onClick={removeBlackout}>
          Remove Blackout
        </Button>
      )}
      {!event.dayOfCancelled ? (
        <Button variant="default" onClick={cancelDayOf}>
          Cancel Today's Tickets
        </Button>
      ) : (
        <Button variant="primary" onClick={removeDayOfCancel}>
          Remove Today's Tickets Cancellation
        </Button>
      )}
    </StickyFooterButtons>
    <Button variant="default" onClick={close}>
      Close
    </Button>
  </div>
);

const GameSummary = ({
  event,
  eventTime,
  ticketsLeft,
  loadingTickets,
}: {
  event: GameDTO;
  eventTime: GameTime;
  ticketsLeft?: number;
  loadingTickets: boolean;
}) => (
  <Paper className="w-100">
    <div className="d-flex justify-content-between align-items-center bcr-gap-4">
      <div>
        <Typography variant="titleMedium" elementStyles={{ weight: "bold" }}>
          {eventTime.date}
        </Typography>
        <Typography>
          {eventTime.day}
          <span className="bcr-ms-2">{eventTime.time}</span>
        </Typography>
      </div>
      <div className="d-flex flex-column align-items-end">
        <MatchUp home={event.teams.home.team} away={event.teams.away.team} description={event.seriesDescription} />
        <Typography className="bcr-mt-1">
          {loadingTickets
            ? "Loading tickets..."
            : ticketsLeft !== undefined
              ? `${ticketsLeft} Ticket(s) Left`
              : "Ticket count unavailable"}
        </Typography>
      </div>
    </div>
  </Paper>
);

const AdminEditEvent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { eventId } = useParams<AdminEditEventUrlProps>();
  const [event, setEvent] = useState<GameDTO>();
  const [eventTime, setEventTime] = useState<GameTime>();
  const [departments, setDepartments] = useState<DepartmentByGameDTO[]>([]);
  const [ticketsLeft, setTicketsLeft] = useState<number>();
  const [loadingTickets, setLoadingTickets] = useState<boolean>(false);

  const getDepartments = async () => {
    try {
      setLoading(true);
      setDepartments([]);
      const res = await getDepartmentsByEvent(eventId!);
      setDepartments(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    const previousLocation = (location.state as RequestixLocationState | undefined)?.previousLocation;
    navigate(previousLocation ?? RouteConstants.ADMIN.EVENTS);
  };

  const fetchTicketCount = async (game: GameDTO) => {
    const homeTeamId = game.teams?.home?.team?.id;
    if (!homeTeamId) {
      setTicketsLeft(undefined);
      return;
    }

    try {
      setLoadingTickets(true);
      const count = await fetchAvailableDigitalTicketCount(game.gamePk, homeTeamId);
      setTicketsLeft(count);
    } catch (e) {
      console.error(e);
      setTicketsLeft(undefined);
    } finally {
      setLoadingTickets(false);
    }
  };

  const blackoutGame = async () => {
    try {
      setLoading(true);
      await addEventBlackout(eventId!);
      setEvent(event ? { ...event, blackedOut: true } : event);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const removeBlackout = async () => {
    try {
      setLoading(true);
      await deleteEventBlackout(eventId!);
      setEvent(event ? { ...event, blackedOut: false } : event);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cancelDayOf = async () => {
    try {
      setLoading(true);
      await cancelDayOfGame(eventId!);
      setEvent(event ? { ...event, dayOfCancelled: true } : event);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const removeDayOfCancel = async () => {
    try {
      setLoading(true);
      await removeDayOfCancellation(eventId!);
      setEvent(event ? { ...event, dayOfCancelled: false } : event);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvent = async (eventId: string) => {
    try {
      setLoading(true);
      const res = await getEvent(eventId);
      setEvent(res);
      setEventTime(getTimezone(res.gameDate, res.venue.timeZone, res.status));
      void fetchTicketCount(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (event?.season && event?.gameType) {
      void getDepartments();
    }
  }, [event?.season, event?.gameType]);

  useEffect(() => {
    if (!!eventId) {
      void fetchEvent(eventId);
    }
  }, [eventId]);

  return (
    <div>
      <Header
        sticky
        title={
          !!event && !!eventTime ? (
            <div className="d-flex align-items-center justify-content-between bcr-gap-2 w-100">
              <div>
                <GameSummary
                  event={event}
                  eventTime={eventTime}
                  ticketsLeft={ticketsLeft}
                  loadingTickets={loadingTickets}
                />
              </div>
              <EventActions
                event={event}
                close={close}
                blackoutGame={blackoutGame}
                removeBlackout={removeBlackout}
                cancelDayOf={cancelDayOf}
                removeDayOfCancel={removeDayOfCancel}
              />
            </div>
          ) : null
        }
      />
      <ScrollContainer className="bcr-p-2 bcr-pb-5">
        {!!event && !!departments.length ? (
          <EventAccess event={event} departments={departments} reload={getDepartments} />
        ) : (
          <div className="d-flex justify-content-center w-100 h-100">
            <Loader type="spinner" size="50px" />
          </div>
        )}
      </ScrollContainer>
    </div>
  );
};

export default AdminEditEvent;
